import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LogIn, Mail, Play, Plus, UserPlus } from "lucide-react";
import { Button, Card, CardHeader, Chip, ThemeToggle } from "@/ui";
import { account, type TeamMember } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useMe } from "@/auth/oidc";
import { useEntitlements, useInviteMember, useMembers } from "@/data/hooks";
import type { Member } from "@/data/services";
import { FieldRow, Meter, Monogram, SampleChip, SkeletonLine, StatusPill } from "./parts";
import { DomainsSection } from "./DomainsSection";
import { WebPropertiesSection } from "./WebPropertiesSection";
import { IntegrationsSection } from "./IntegrationsSection";
import { ActivitySection } from "./ActivitySection";

/** Two-letter initials from a name or email. */
function initialsOf(s: string): string {
  const base = (s.split("@")[0] ?? s).trim();
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  const a = parts[0]?.[0] ?? "";
  const b = parts.length > 1 ? parts[parts.length - 1]![0]! : (parts[0]?.[1] ?? "");
  return (a + b).toUpperCase() || "Y";
}

/** Friendly display name from a member user_id (id or email). */
function memberName(id: string): string {
  const local = id.split("@")[0] ?? id;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase() + w.slice(1))
    .join(" ") || id;
}

type Section =
  | "profile"
  | "plan"
  | "team"
  | "domains"
  | "properties"
  | "integrations"
  | "activity"
  | "appearance";

const SECTIONS: { value: Section; label: string }[] = [
  { value: "profile", label: "Profile" },
  { value: "plan", label: "Plan & usage" },
  { value: "team", label: "Team" },
  { value: "domains", label: "Domains" },
  { value: "properties", label: "Properties" },
  { value: "integrations", label: "Integrations" },
  { value: "activity", label: "Activity" },
  { value: "appearance", label: "Appearance" },
];

const ROLE_COPY: Record<TeamMember["role"], string> = {
  owner: "Full access · billing",
  editor: "Make & publish",
  viewer: "Read-only",
};

export function YouScreen() {
  const { section } = useParams<{ section?: string }>();
  const nav = useNavigate();
  const active = (SECTIONS.find((s) => s.value === section)?.value ?? "profile") as Section;

  const me = useMe();
  const ws = useWorkspace();
  const liveUser = me.isAuthenticated && me.user;
  const headerName = liveUser ? me.user!.name : account.user.name;
  const headerInitials = liveUser ? initialsOf(me.user!.name || me.user!.email) : account.user.initials;
  const workspaceName = ws.current?.name ?? account.workspace.name;

  return (
    <div className="stagger space-y-6">
      <header className="flex items-center gap-4">
        <Monogram initials={headerInitials} size="lg" />
        <div className="min-w-0">
          <p className="eyebrow mb-1">You</p>
          <h1 className="flex items-center gap-2 truncate text-[22px] font-semibold tracking-tight text-ink-strong md:text-[26px]">
            {headerName}
            {!liveUser && <SampleChip />}
          </h1>
          <p className="truncate text-[14px] text-muted">
            {workspaceName} · {ws.live ? "Live workspace" : account.workspace.plan}
          </p>
        </div>
      </header>

      <div className="flex flex-col gap-6 md:flex-row md:gap-8">
        {/* Section nav — sidebar on desktop, segmented scroller on mobile */}
        <nav className="md:w-48 md:shrink-0">
          <div className="hidden md:flex md:flex-col md:gap-0.5">
            {SECTIONS.map((s) => (
              <button
                key={s.value}
                onClick={() => nav(`/you/${s.value}`)}
                className={
                  "rounded-btn px-3 py-2 text-left text-[14px] transition-colors " +
                  (s.value === active
                    ? "bg-paper-2 font-medium text-ink-strong dark:bg-white/5"
                    : "text-muted hover:text-ink")
                }
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="no-scrollbar -mx-1 flex gap-1 overflow-x-auto px-1 md:hidden">
            {SECTIONS.map((s) => (
              <button
                key={s.value}
                onClick={() => nav(`/you/${s.value}`)}
                className={
                  "relative whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors " +
                  (s.value === active ? "text-ink-strong" : "text-muted hover:text-ink")
                }
              >
                {s.label}
                {s.value === active && (
                  <span className="absolute inset-x-3 -bottom-px h-[2px] rounded-full bg-accent" />
                )}
              </button>
            ))}
          </div>
        </nav>

        <div className="min-w-0 flex-1">
          {active === "profile" && <ProfilePanel />}
          {active === "plan" && <PlanPanel />}
          {active === "team" && <TeamPanel />}
          {active === "domains" && <DomainsSection />}
          {active === "properties" && <WebPropertiesSection />}
          {active === "integrations" && <IntegrationsSection />}
          {active === "activity" && <ActivitySection />}
          {active === "appearance" && <AppearancePanel />}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- Profile -------------------------------- */
function ProfilePanel() {
  const me = useMe();
  const ws = useWorkspace();

  if (me.isLoading) {
    return (
      <div className="space-y-4">
        <Card className="p-5">
          <CardHeader title="Account" eyebrow="Who you are" className="px-0 pt-0" />
          <div className="flex items-center gap-4 py-1">
            <span className="h-14 w-14 shrink-0 animate-pulse rounded-full bg-paper-2 dark:bg-white/5" />
            <div className="space-y-2">
              <SkeletonLine className="h-4 w-40" />
              <SkeletonLine className="h-3 w-56" />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const live = me.isAuthenticated && me.user;
  const name = live ? me.user!.name : account.user.name;
  const email = live ? me.user!.email : account.user.email;
  const initials = live ? initialsOf(name || email) : account.user.initials;
  const workspaceName = ws.current?.name ?? account.workspace.name;

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <CardHeader
          title="Account"
          eyebrow="Who you are"
          className="px-0 pt-0"
          action={!live ? <SampleChip /> : undefined}
        />
        <div className="flex items-center gap-4 py-1">
          <Monogram initials={initials} size="lg" />
          <div className="min-w-0">
            <p className="truncate text-[16px] font-semibold tracking-tight text-ink-strong">{name}</p>
            <p className="truncate text-[13px] text-muted">{email}</p>
          </div>
          {live ? (
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => me.signOut()}>
              Sign out
            </Button>
          ) : (
            <Button variant="coral" size="sm" className="ml-auto" onClick={() => me.signIn()}>
              <LogIn className="h-4 w-4" strokeWidth={1.75} /> Sign in
            </Button>
          )}
        </div>
        <div className="mt-2 divide-y divide-hairline/60 border-t border-hairline">
          <FieldRow label="Email">{email}</FieldRow>
          <FieldRow label="Identity">
            {live ? <StatusPill tone="ok">ZITADEL</StatusPill> : <StatusPill tone="muted">Signed out</StatusPill>}
          </FieldRow>
        </div>
        {!live && (
          <p className="mt-3 text-[12px] text-subtle">
            Sign in to see your real account, plan and team. Showing a sample profile for now.
          </p>
        )}
      </Card>

      <Card className="p-5">
        <CardHeader
          title="Workspace"
          eyebrow="Where you work"
          className="px-0 pt-0"
          action={!ws.live ? <SampleChip /> : undefined}
        />
        <div className="divide-y divide-hairline/60">
          <FieldRow label="Name">{workspaceName}</FieldRow>
          <FieldRow label="Properties">
            {ws.live ? `${ws.properties.length} properties` : `${account.team.length} people`}
          </FieldRow>
        </div>
      </Card>
    </div>
  );
}

/* ------------------------------ Plan & usage ----------------------------- */
function PlanPanel() {
  const me = useMe();
  const ent = useEntitlements();

  if (ent.isLoading) {
    return (
      <div className="space-y-4">
        <Card className="p-5">
          <div className="space-y-2">
            <SkeletonLine className="h-3 w-20" />
            <SkeletonLine className="h-5 w-40" />
            <SkeletonLine className="h-3 w-32" />
          </div>
        </Card>
        <Card className="p-5">
          <CardHeader title="Usage" eyebrow="This billing period" className="px-0 pt-0" />
          <div className="space-y-2 pt-1">
            <SkeletonLine className="h-3 w-full" />
            <SkeletonLine className="h-1.5 w-full" />
          </div>
        </Card>
      </div>
    );
  }

  // 401 / error / signed-out -> graceful sample fallback.
  const data = ent.data;
  const isSample = !data;
  const planName = data?.plan ?? account.plan.name;
  const pageLimit = data?.page_limit ?? 50;
  const pagesUsed = data?.pages_used ?? 12;

  return (
    <div className="space-y-4">
      <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="eyebrow mb-1 flex items-center gap-2">
            Current plan {isSample && <SampleChip />}
          </p>
          <p className="text-[20px] font-semibold capitalize tracking-tight text-ink-strong">
            {planName}
          </p>
          <p className="text-[13px] text-muted">
            {isSample ? "Sign in to load your real plan" : "Dev plan — no live billing yet"}
          </p>
        </div>
        <div className="flex gap-2">
          {isSample ? (
            <Button variant="coral" size="md" onClick={() => me.signIn()}>
              <LogIn className="h-4 w-4" strokeWidth={1.75} /> Sign in
            </Button>
          ) : (
            <div className="flex flex-col items-stretch gap-1.5 sm:items-end">
              <div className="flex gap-2">
                <Button variant="ghost" size="md" disabled>
                  Manage billing
                </Button>
                <Button variant="coral" size="md" disabled>
                  Upgrade
                </Button>
              </div>
              <span className="text-[11px] text-subtle">Billing isn&rsquo;t live in this environment yet.</span>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <CardHeader
          title="Published pages"
          eyebrow="Usage"
          className="px-0 pt-0"
          action={isSample ? <SampleChip /> : undefined}
        />
        <div className="space-y-4 pt-1">
          <Meter label="Published pages" used={pagesUsed} limit={pageLimit} unit="pages" />
        </div>
        <p className="mt-4 border-t border-hairline pt-3 text-[12px] text-subtle">
          {isSample
            ? "Sample usage shown — sign in to see your real page allowance."
            : "We'll warn you before you hit your page limit."}
        </p>
      </Card>
    </div>
  );
}

/* --------------------------------- Team ---------------------------------- */
function TeamPanel() {
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState("");
  const ws = useWorkspace();
  const projectId = ws.currentProjectId;
  const membersQ = useMembers(projectId);
  const invite = useInviteMember(projectId);

  const members: Member[] = membersQ.data?.items ?? [];
  const workspaceName = ws.current?.name ?? account.workspace.name;

  const closeForm = () => {
    setInviting(false);
    setEmail("");
    invite.reset();
  };

  if (membersQ.isLoading) {
    return (
      <Card className="overflow-hidden">
        <div className="border-b border-hairline px-5 py-4">
          <SkeletonLine className="h-3 w-14" />
        </div>
        <div className="divide-y divide-hairline/60">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 px-5 py-4">
              <span className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-paper-2 dark:bg-white/5" />
              <div className="flex-1 space-y-1.5">
                <SkeletonLine className="h-3.5 w-32" />
                <SkeletonLine className="h-3 w-48" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-hairline px-5 py-4">
        <div>
          <p className="eyebrow mb-1">Team</p>
          <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">
            {members.length} {members.length === 1 ? "person" : "people"} in {workspaceName}
          </h3>
        </div>
        {!inviting && (
          <Button variant="ghost" size="sm" onClick={() => setInviting(true)} disabled={!projectId}>
            <UserPlus className="h-4 w-4" strokeWidth={1.75} /> Invite
          </Button>
        )}
      </div>

      {inviting && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const value = email.trim();
            if (!value || invite.isPending) return;
            invite.mutate({ email: value }, { onSuccess: closeForm });
          }}
          className="animate-fade-up border-b border-hairline bg-paper-2/60 px-5 py-4 dark:bg-white/5"
        >
          <label className="eyebrow mb-1.5 block">Invite by email</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-btn border border-hairline bg-paper px-3 focus-within:border-accent">
              <Mail className="h-4 w-4 shrink-0 text-subtle" strokeWidth={1.5} />
              <input
                autoFocus
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={invite.isPending}
                placeholder="teammate@aigen.solutions"
                className="h-11 w-full bg-transparent text-[14px] text-ink outline-none placeholder:text-subtle disabled:opacity-60"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" variant="coral" size="md" disabled={!email.trim() || invite.isPending}>
                <Plus className="h-4 w-4" strokeWidth={1.75} /> {invite.isPending ? "Sending…" : "Send invite"}
              </Button>
              <Button type="button" variant="quiet" size="md" onClick={closeForm} disabled={invite.isPending}>
                Cancel
              </Button>
            </div>
          </div>
          {invite.isError ? (
            <p className="mt-2 text-[12px] text-accent">
              {(invite.error as Error)?.message || "Couldn't send the invite. Try again."}
            </p>
          ) : (
            <p className="mt-2 text-[12px] text-subtle">
              They&apos;ll join as an editor — change their role any time.
            </p>
          )}
        </form>
      )}

      {members.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <p className="text-[14px] font-medium text-ink-strong">No teammates yet</p>
          <p className="mt-1 text-[13px] text-muted">
            Invite someone to make and publish pages alongside you.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-hairline/60">
          {members.map((m) => {
            const display = memberName(m.email);
            const pending = m.status === "pending";
            return (
              <div key={m.id} className="flex items-center gap-3 px-5 py-4">
                <Monogram initials={initialsOf(display)} size="sm" className={pending ? "opacity-70" : undefined} />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate text-[14px] font-medium text-ink-strong">
                    {display}
                    {pending && <StatusPill tone="pending">Pending</StatusPill>}
                  </p>
                  <p className="truncate text-[13px] text-muted">{m.email}</p>
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <Chip active={m.role === "owner"} className="capitalize">
                    {m.role}
                  </Chip>
                  <span className="hidden font-mono text-[10px] text-subtle sm:block">
                    {ROLE_COPY[m.role as TeamMember["role"]] ?? "Member"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/* ------------------------------ Appearance ------------------------------- */
function AppearancePanel() {
  const nav = useNavigate();
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <CardHeader title="Theme" eyebrow="Appearance" className="px-0 pt-0" />
        <p className="mb-4 text-[13px] text-muted">
          Warm light for daytime, ink for the dark. Auto follows your system.
        </p>
        <ThemeToggle />
      </Card>

      <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="eyebrow mb-1">Present mode</p>
          <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">
            Show the numbers, full screen
          </h3>
          <p className="mt-0.5 text-[13px] text-muted">
            A calm, zoomed-out view for standups and the big screen.
          </p>
        </div>
        <Button variant="ink" size="md" onClick={() => nav("/present")} className="shrink-0">
          <Play className="h-4 w-4" strokeWidth={1.75} /> Enter present mode
        </Button>
      </Card>
    </div>
  );
}
